/**
 * B115 — the human confirmation code (Pathik 2026-07-30: "the long order
 * ids will not work from a guest point of view"). A short code a buyer can
 * quote over a phone, type at a counter, read off a receipt — minted at
 * order confirmation ALONGSIDE the order id. The id stays the system key;
 * no state-machine or ledger semantics change.
 *
 * Shape: 8 glyphs from a Crockford-style alphabet with every ambiguous
 * glyph removed (no I/L/O/U, no 0/1), upper-case, grouped for reading
 * (`HF7K-2Q9M`). 30^8 ≈ 6.5e11 per tenant — collisions are rare and the
 * mint retries through the put-if-absent claim until a free code lands.
 *
 * SECURITY (the B115 wall, pinned where the lookup door lands): a short
 * code is guessable, so it is NEVER a lookup key on its own — order lookup
 * requires code + buyer email, rate-limited, answering the uniform absent
 * shape. Nothing in this module weakens Inv 10 or the B25c/d fences: the
 * claim store exists solely to keep codes unique per tenant.
 *
 * PURITY: crypto randomness is I/O — the generator draws from an injected
 * RandomBytes source (node:crypto lives at the edge, fakes in tests).
 */
import { tenantRowKey } from "./keys.js";

/** Crockford-style, minus EVERY ambiguous glyph: no I, L, O, U, 0, 1. */
export const CONFIRMATION_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";

const GLYPHS = CONFIRMATION_CODE_ALPHABET.length; // 30
/** Largest multiple of GLYPHS below 256 — bytes at/above re-introduce modulo bias and are redrawn. */
const UNBIASED_CEILING = 256 - (256 % GLYPHS); // 240
const CODE_LENGTH = 8;
const GROUP = 4;

/** Injected randomness — crypto is I/O to the domain (devices.ts precedent). */
export type RandomBytes = (n: number) => Uint8Array;

const ALPHABET_SET = new Set(CONFIRMATION_CODE_ALPHABET);

/**
 * Draw an 8-glyph code from the byte source via rejection sampling: a byte
 * below the unbiased ceiling maps to `byte % 30`; anything at/above is
 * skipped and redrawn, so every glyph stays equally likely.
 */
export function generateConfirmationCode(random: RandomBytes): string {
  const glyphs: string[] = [];
  while (glyphs.length < CODE_LENGTH) {
    const batch = random(CODE_LENGTH - glyphs.length);
    for (const byte of batch) {
      if (glyphs.length >= CODE_LENGTH) break;
      if (byte >= UNBIASED_CEILING) continue;
      glyphs.push(CONFIRMATION_CODE_ALPHABET[byte % GLYPHS]!);
    }
  }
  return `${glyphs.slice(0, GROUP).join("")}-${glyphs.slice(GROUP).join("")}`;
}

/**
 * Accept what humans type — lower-case, stray spaces, missing or misplaced
 * hyphens — and answer the ONE canonical grouped form. Anything carrying an
 * out-of-alphabet glyph or the wrong glyph count is undefined: the
 * normalizer never guesses (I/O/1/0 are invalid, not "corrected" — they
 * cannot appear in a real code).
 */
export function normalizeConfirmationCode(input: string): string | undefined {
  const bare = input.toUpperCase().replace(/[\s-]/g, "");
  if (bare.length !== CODE_LENGTH) return undefined;
  for (const ch of bare) if (!ALPHABET_SET.has(ch)) return undefined;
  return `${bare.slice(0, GROUP)}-${bare.slice(GROUP)}`;
}

/** Ran out of mint attempts — statistically impossible unless the store is saturated or randomness is broken. */
export class ConfirmationCodeExhaustedError extends Error {
  constructor(tenantId: string, attempts: number) {
    super(
      `Could not mint a unique confirmation code for tenant ${tenantId} after ${attempts} attempts — the code space is saturated or the randomness source is broken`,
    );
    this.name = "ConfirmationCodeExhaustedError";
  }
}

/**
 * Uniqueness authority — put-if-absent per (tenant, code), the house
 * conditional-write pattern. `claim` answers true when this call took the
 * code, false when it was already held. No update or delete path exists:
 * a claimed code is claimed forever (codes are stamped immutably).
 */
export interface ConfirmationCodeStore {
  claim(tenantId: string, code: string, orderId: string): Promise<boolean>;
  /**
   * B115(a) — the lookup door's read: the orderId a claimed code maps to,
   * or undefined. Takes the CANONICAL grouped form (callers normalize).
   * NEVER exposed on its own as a §11 read — the door demands code +
   * buyer email and rate-limits (see order-lookup.ts).
   */
  find(tenantId: string, code: string): Promise<string | undefined>;
}

/** Reference implementation — the DynamoDB adapter must match it. */
export class InMemoryConfirmationCodeStore implements ConfirmationCodeStore {
  private readonly rows = new Map<string, string>();

  claim(tenantId: string, code: string, orderId: string): Promise<boolean> {
    const key = tenantRowKey(tenantId, code);
    if (this.rows.has(key)) return Promise.resolve(false);
    this.rows.set(key, orderId);
    return Promise.resolve(true);
  }

  find(tenantId: string, code: string): Promise<string | undefined> {
    return Promise.resolve(this.rows.get(tenantRowKey(tenantId, code)));
  }
}

export interface MintConfirmationCodeInput {
  readonly store: ConfirmationCodeStore;
  readonly random: RandomBytes;
  readonly tenantId: string;
  readonly orderId: string;
  /** Defaults to 16 — at 30^8 codes per tenant, a real collision streak this long means something is broken. */
  readonly maxAttempts?: number;
}

/**
 * Mint a tenant-unique code: generate → claim, retrying past collisions.
 * Exhaustion throws by name — a duplicate code is never returned.
 */
export async function mintUniqueConfirmationCode(input: MintConfirmationCodeInput): Promise<string> {
  const attempts = input.maxAttempts ?? 16;
  for (let i = 0; i < attempts; i += 1) {
    const code = generateConfirmationCode(input.random);
    if (await input.store.claim(input.tenantId, code, input.orderId)) return code;
  }
  throw new ConfirmationCodeExhaustedError(input.tenantId, attempts);
}
